import React from 'react';
import {
  Header,
  SpaceBetween,
  Box,
  TextContent,
  ColumnLayout,
  Button,
  Link,
  Alert,
  AnchorNavigation,
  AppLayout,
  ContentLayout,
  Grid,
  ExpandableSection,
  Tabs,
} from '@cloudscape-design/components';
import AWSCDKIcon from '../../components/AWSCDKIcon';
import { useTheme } from '../../context/ThemeContext';
import { useModuleVisit } from '../../hooks/useAnalytics';

const DEFAULT_BANNER = "linear-gradient(135deg, rgb(71, 17, 118) 3%, rgb(131, 57, 157) 44%, rgb(149, 85, 182) 69%, rgb(145, 134, 215) 94%)";
const SUMMIT_BANNER_LIGHT = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_1@2x.png')";
const SUMMIT_BANNER_DARK = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_2.png')";

// Main DeploymentPage content component
function DeploymentPageContent() {
  const { isSummit, isDark } = useTheme();
  useModuleVisit('deployment');
  const banner = isSummit ? (isDark ? SUMMIT_BANNER_DARK : SUMMIT_BANNER_LIGHT) : DEFAULT_BANNER;
  return (
    <ContentLayout
      defaultPadding
      disableOverlap
      headerBackgroundStyle={banner}
      headerVariant="high-contrast"
      header={
        <Box padding={{ vertical: "xxxl" }}>
          <Grid
            gridDefinition={[
              { colspan: { default: 12, s: 8 } }
            ]}
          >
            <Box padding="s">
              <Box display="flex" alignItems="center" margin={{ bottom: "s" }}>
                <AWSCDKIcon width={60} height={60} />
              </Box>
              <Box
                fontSize="display-l"
                fontWeight="bold"
                variant="h1"
                padding="n"
              >
                Infrastructure as Code & Deployment
              </Box>
              <Box
                variant="p"
                color="text-body-secondary"
                margin={{ top: "xs", bottom: "l" }}
              >
                ResearchIQ's infrastructure is built using AWS CDK (Cloud Development Kit), enabling 
                infrastructure as code with the power and flexibility of familiar programming languages. 
                This approach ensures consistent, repeatable, and scalable deployments.
              </Box>
            </Box>
          </Grid>
        </Box>
      }
    >
      <SpaceBetween size="xl">
        {/* Benefits of AWS CDK Section */}
        <div id="cdk-benefits">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ 
              border: 'none', 
              borderTop: '1px solid #e9ebed', 
              margin: '3.5rem 0 3.5rem 0' 
            }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Benefits of AWS CDK
            </Header>
          </Box>
          
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <TextContent>
                <h3>Familiar Programming Languages</h3>
                <p>
                  Define infrastructure using TypeScript, Python, Java, C#, or Go. 
                  ResearchIQ uses TypeScript, allowing our development team to leverage 
                  existing skills and tooling for infrastructure development.
                </p>
                
                <h3>Higher-Level Abstractions</h3>
                <p>
                  CDK constructs encapsulate AWS best practices and reduce boilerplate code. 
                  Complex configurations that would require hundreds of lines of CloudFormation 
                  can be expressed in just a few lines of CDK code.
                </p>
                
                <h3>Built-in Best Practices</h3>
                <p>
                  CDK constructs come with sensible defaults and built-in security best practices. 
                  This helped ResearchIQ implement secure configurations without deep AWS expertise.
                </p>
              </TextContent>
            </div>
            
            <div>
              <TextContent>
                <h3>Powerful Customization</h3>
                <p>
                  Customize and extend constructs to fit specific needs. ResearchIQ created 
                  custom constructs for common patterns, enabling code reuse across different 
                  components of the application.
                </p>
                
                <h3>Integration with Development Tools</h3>
                <p>
                  Use familiar IDEs, testing frameworks, and package managers. Infrastructure 
                  code benefits from the same development practices as application code, 
                  including unit testing and code reviews.
                </p>
                
                <h3>Faster Development</h3>
                <p>
                  Accelerate cloud development with IDE features like auto-complete and 
                  inline documentation. Catch errors at compile time rather than deployment time.
                </p>
              </TextContent>
            </div>
          </ColumnLayout>
        </div>

        {/* Deployment Architecture Section */}
        <div id="deployment-architecture">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ 
              border: 'none', 
              borderTop: '1px solid #e9ebed', 
              margin: '0 0 1.5rem 0' 
            }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              Getting Started with AWS CDK
            </Header>
          </Box>
              
          <TextContent>
            <p>
              Ready to build your own infrastructure as code? AWS CDK makes it easy to get started 
              with cloud development using familiar programming languages.
            </p>
          </TextContent>

          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box padding="l" variant="container">
                <SpaceBetween size="m">
                  <Header variant="h3">Learn AWS CDK</Header>
                  <TextContent>
                    <p>
                      Explore comprehensive documentation, tutorials, and examples to master 
                      infrastructure as code with AWS CDK.
                    </p>
                  </TextContent>
                  <Button 
                    variant="primary" 
                    iconName="external"
                    href="https://aws.amazon.com/cdk/"
                    target="_blank"
                  >
                    Visit AWS CDK
                  </Button>
                </SpaceBetween>
              </Box>
            </div>
            
            <div>
              <Box padding="l" variant="container">
                <SpaceBetween size="m">
                  <Header variant="h3">CDK Workshop</Header>
                  <TextContent>
                    <p>
                      Follow hands-on workshops to build your first CDK application and 
                      learn best practices for infrastructure development.
                    </p>
                  </TextContent>
                  <Button 
                    variant="primary" 
                    iconName="external"
                    href="https://cdkworkshop.com/"
                    target="_blank"
                  >
                    Start Workshop
                  </Button>
                </SpaceBetween>
              </Box>
            </div>
          </ColumnLayout>
        </div>

        {/* Infrastructure Code Section */}
        <div id="infrastructure-code">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <hr style={{ 
              border: 'none', 
              borderTop: '1px solid #e9ebed', 
              margin: '3rem 0 2rem 0' 
            }} />
            <Header variant="h1" margin={{ bottom: "l" }}>
              ResearchIQ Infrastructure Code
            </Header>
            <TextContent>
              <p>To deploy the stack run <code>cdk deploy</code> which reads the app.py on how to deploy the stacks</p>
            </TextContent>
          </Box>
          
          <ExpandableSection headerText="CDK Application Structure" variant="container" headerDescription="Complete infrastructure code for ResearchIQ deployment">
            <Tabs
              tabs={[
                {
                  label: "app.py",
                  id: "app-py",
                  content: (
                    <Box margin={{ vertical: "m" }}>
                      <pre style={{ 
                        backgroundColor: "var(--color-background-container-content-6u8rvp, #f8f9fa)", 
                        padding: "1rem", 
                        borderRadius: "4px", 
                        overflow: "auto",
                        fontSize: "0.875rem",
                        lineHeight: "1.4"
                      }}>
{`#!/usr/bin/env python3

import os
import aws_cdk as cdk
from lib.network_stack import NetworkStack
from lib.storage_stack import StorageStack
from lib.ai_stack import AIStack
from lib.compute_stack import ComputeStack
from lib.fargate_compute_stack import FargateComputeStack
from lib.frontend_stack import FrontendStack

app = cdk.App()

# Environment configuration
env = cdk.Environment(
    account=os.environ.get("CDK_DEFAULT_ACCOUNT"),
    region="ap-southeast-2"  # Sydney region
)

# Configuration: Choose compute backend
# Set USE_FARGATE=true to use ECS Fargate, false for Lambda
use_fargate = os.environ.get("USE_FARGATE", "true").lower() == "true"

print(f"🚀 Deploying DocuMind with {'ECS Fargate' if use_fargate else 'Lambda'} compute backend")

# Create stacks with dependencies
network_stack = NetworkStack(app, "DocuMindNetworkStack", env=env)

storage_stack = StorageStack(app, "DocuMindStorageStack",
    vpc=network_stack.vpc,
    env=env
)
storage_stack.add_dependency(network_stack)

ai_stack = AIStack(app, "DocuMindAIStack",
    vpc=network_stack.vpc,
    document_bucket=storage_stack.document_bucket,
    env=env
)
ai_stack.add_dependency(storage_stack)

# Choose compute stack based on configuration
if use_fargate:
    # Use ECS Fargate for Strands agents (recommended for production)
    compute_stack = FargateComputeStack(app, "DocuMindFargateComputeStack",
        vpc=network_stack.vpc,
        document_bucket=storage_stack.document_bucket,
        document_metadata_table=storage_stack.document_metadata_table,
        research_results_table=storage_stack.research_results_table,
        demo_state_table=storage_stack.demo_state_table,
        knowledge_base=ai_stack.knowledge_base_id,
        env=env
    )
else:
    # Use Lambda (legacy, has limitations for Strands agents)
    compute_stack = ComputeStack(app, "DocuMindComputeStack",
        vpc=network_stack.vpc,
        document_bucket=storage_stack.document_bucket,
        document_metadata_table=storage_stack.document_metadata_table,
        research_results_table=storage_stack.research_results_table,
        demo_state_table=storage_stack.demo_state_table,
        knowledge_base=ai_stack.knowledge_base_id,
        env=env
    )

compute_stack.add_dependency(ai_stack)

frontend_stack = FrontendStack(app, "DocuMindFrontendStack",
    vpc=network_stack.vpc,
    env=env
)
frontend_stack.add_dependency(compute_stack)

app.synth()`}
                      </pre>
                    </Box>
                  )
                },
                {
                  label: "Network Stack",
                  id: "network-stack",
                  content: (
                    <Box margin={{ vertical: "m" }}>
                      <pre style={{ 
                        backgroundColor: "var(--color-background-container-content-6u8rvp, #f8f9fa)", 
                        padding: "1rem", 
                        borderRadius: "4px", 
                        overflow: "auto",
                        fontSize: "0.875rem",
                        lineHeight: "1.4"
                      }}>
{`# Network Stack - VPC and Networking Infrastructure

from aws_cdk import (
    Stack,
    aws_ec2 as ec2,
)
from constructs import Construct

class NetworkStack(Stack):
    def __init__(self, scope: Construct, construct_id: str, **kwargs) -> None:
        super().__init__(scope, construct_id, **kwargs)
        
        # Create VPC with public and private subnets
        self.vpc = ec2.Vpc(
            self, "DocuMindVPC",
            max_azs=2,
            nat_gateways=1,
            subnet_configuration=[
                ec2.SubnetConfiguration(
                    name="PublicSubnet",
                    subnet_type=ec2.SubnetType.PUBLIC,
                    cidr_mask=24
                ),
                ec2.SubnetConfiguration(
                    name="PrivateSubnet", 
                    subnet_type=ec2.SubnetType.PRIVATE_WITH_EGRESS,
                    cidr_mask=24
                )
            ]
        )`}
                      </pre>
                    </Box>
                  )
                },
                {
                  label: "Storage Stack",
                  id: "storage-stack",
                  content: (
                    <Box margin={{ vertical: "m" }}>
                      <pre style={{ 
                        backgroundColor: "var(--color-background-container-content-6u8rvp, #f8f9fa)", 
                        padding: "1rem", 
                        borderRadius: "4px", 
                        overflow: "auto",
                        fontSize: "0.875rem",
                        lineHeight: "1.4"
                      }}>
{`# Storage Stack - S3 and DynamoDB Resources

from aws_cdk import (
    Stack,
    aws_s3 as s3,
    aws_dynamodb as dynamodb,
    RemovalPolicy
)
from constructs import Construct

class StorageStack(Stack):
    def __init__(self, scope: Construct, construct_id: str, vpc, **kwargs) -> None:
        super().__init__(scope, construct_id, **kwargs)
        
        # S3 bucket for document storage
        self.document_bucket = s3.Bucket(
            self, "DocumentBucket",
            versioned=True,
            encryption=s3.BucketEncryption.S3_MANAGED,
            removal_policy=RemovalPolicy.DESTROY,
            auto_delete_objects=True
        )
        
        # DynamoDB tables
        self.document_metadata_table = dynamodb.Table(
            self, "DocumentMetadataTable",
            partition_key=dynamodb.Attribute(
                name="document_id",
                type=dynamodb.AttributeType.STRING
            ),
            billing_mode=dynamodb.BillingMode.PAY_PER_REQUEST,
            removal_policy=RemovalPolicy.DESTROY
        )
        
        self.research_results_table = dynamodb.Table(
            self, "ResearchResultsTable", 
            partition_key=dynamodb.Attribute(
                name="session_id",
                type=dynamodb.AttributeType.STRING
            ),
            billing_mode=dynamodb.BillingMode.PAY_PER_REQUEST,
            removal_policy=RemovalPolicy.DESTROY
        )`}
                      </pre>
                    </Box>
                  )
                },
                {
                  label: "AI Stack",
                  id: "ai-stack",
                  content: (
                    <Box margin={{ vertical: "m" }}>
                      <pre style={{ 
                        backgroundColor: "var(--color-background-container-content-6u8rvp, #f8f9fa)", 
                        padding: "1rem", 
                        borderRadius: "4px", 
                        overflow: "auto",
                        fontSize: "0.875rem",
                        lineHeight: "1.4"
                      }}>
{`# AI Stack - Bedrock Knowledge Base and IAM Roles

from aws_cdk import (
    Stack,
    aws_iam as iam,
    CfnParameter
)

class AIStack(Stack):
    def __init__(self, scope: Construct, construct_id: str, vpc, document_bucket, **kwargs) -> None:
        super().__init__(scope, construct_id, **kwargs)

        # Create a parameter for the Bedrock Knowledge Base ID
        kb_id_param = CfnParameter(
            self, "BedrockKnowledgeBaseId",
            type="String",
            description="ID of the manually created Bedrock Knowledge Base",
            default="YOUR_KB_ID_HERE"
        )
        
        self.knowledge_base_id = kb_id_param.value_as_string

        # Create IAM role for Bedrock Knowledge Base
        kb_role = iam.Role(
            self, "BedrockKnowledgeBaseRole",
            assumed_by=iam.ServicePrincipal("bedrock.amazonaws.com"),
            description="Role for Bedrock Knowledge Base to access S3"
        )

        # Grant permissions to the role
        document_bucket.grant_read(kb_role)
        
        # Add permissions for Bedrock access
        kb_role.add_to_policy(
            iam.PolicyStatement(
                actions=[
                    "bedrock:ListKnowledgeBases",
                    "bedrock:GetKnowledgeBase", 
                    "bedrock:RetrieveAndGenerate"
                ],
                resources=["*"]
            )
        )`}
                      </pre>
                    </Box>
                  )
                },
                {
                  label: "Fargate Stack",
                  id: "fargate-stack",
                  content: (
                    <Box margin={{ vertical: "m" }}>
                      <pre style={{ 
                        backgroundColor: "var(--color-background-container-content-6u8rvp, #f8f9fa)", 
                        padding: "1rem", 
                        borderRadius: "4px", 
                        overflow: "auto",
                        fontSize: "0.875rem",
                        lineHeight: "1.4"
                      }}>
{`# Fargate Compute Stack - ECS Container Deployment

from aws_cdk import (
    Stack,
    aws_ecs as ecs,
    aws_iam as iam,
    Duration
)

class FargateComputeStack(Stack):
    def __init__(self, scope: Construct, construct_id: str, vpc, document_bucket,
                 document_metadata_table, research_results_table, demo_state_table,
                 knowledge_base, **kwargs) -> None:
        super().__init__(scope, construct_id, **kwargs)
        
        # ECS Cluster
        cluster = ecs.Cluster(
            self, "DocuMindCluster",
            vpc=vpc,
            container_insights=True
        )
        
        # Task execution role
        execution_role = iam.Role(
            self, "TaskExecutionRole",
            assumed_by=iam.ServicePrincipal("ecs-tasks.amazonaws.com"),
            managed_policies=[
                iam.ManagedPolicy.from_aws_managed_policy_name("service-role/AmazonECSTaskExecutionRolePolicy")
            ]
        )
        
        # Task role with permissions
        task_role = iam.Role(
            self, "TaskRole", 
            assumed_by=iam.ServicePrincipal("ecs-tasks.amazonaws.com")
        )
        
        # Grant permissions
        document_bucket.grant_read_write(task_role)
        document_metadata_table.grant_read_write_data(task_role)
        research_results_table.grant_read_write_data(task_role)
        
        # Fargate task definition
        task_definition = ecs.FargateTaskDefinition(
            self, "TaskDefinition",
            memory_limit_mib=4096,
            cpu=2048,
            execution_role=execution_role,
            task_role=task_role
        )`}
                      </pre>
                    </Box>
                  )
                },
                {
                  label: "Frontend Stack",
                  id: "frontend-stack",
                  content: (
                    <Box margin={{ vertical: "m" }}>
                      <pre style={{ 
                        backgroundColor: "var(--color-background-container-content-6u8rvp, #f8f9fa)", 
                        padding: "1rem", 
                        borderRadius: "4px", 
                        overflow: "auto",
                        fontSize: "0.875rem",
                        lineHeight: "1.4"
                      }}>
{`# Frontend Stack - CloudFront and S3 Website Hosting

from aws_cdk import (
    Stack,
    aws_s3 as s3,
    aws_cloudfront as cloudfront,
    aws_cloudfront_origins as origins,
    aws_s3_deployment as s3deploy,
    RemovalPolicy
)

class FrontendStack(Stack):
    def __init__(self, scope: Construct, construct_id: str, vpc, **kwargs) -> None:
        super().__init__(scope, construct_id, **kwargs)
        
        # S3 bucket for website hosting
        website_bucket = s3.Bucket(
            self, "WebsiteBucket",
            website_index_document="index.html",
            website_error_document="error.html",
            public_read_access=True,
            removal_policy=RemovalPolicy.DESTROY,
            auto_delete_objects=True
        )
        
        # CloudFront distribution
        distribution = cloudfront.Distribution(
            self, "Distribution",
            default_behavior=cloudfront.BehaviorOptions(
                origin=origins.S3Origin(website_bucket),
                viewer_protocol_policy=cloudfront.ViewerProtocolPolicy.REDIRECT_TO_HTTPS,
                cache_policy=cloudfront.CachePolicy.CACHING_OPTIMIZED
            ),
            default_root_object="index.html"
        )
        
        # Deploy website files
        s3deploy.BucketDeployment(
            self, "DeployWebsite",
            sources=[s3deploy.Source.asset("../frontend/build")],
            destination_bucket=website_bucket,
            distribution=distribution,
            distribution_paths=["/*"]
        )`}
                      </pre>
                    </Box>
                  )
                }
              ]}
            />
          </ExpandableSection>
        </div>
      </SpaceBetween>
    </ContentLayout>
  );
}

// Tools component for right-side navigation
function DeploymentPageTools() {
  return (
    <div>
      <Header variant="h3">
        <span id="navigation-header">On this page</span>
      </Header>
      <AnchorNavigation
        anchors={[
          {
            text: "AWS CDK Overview",
            href: "#cdk-overview",
            level: 1
          },
          {
            text: "Benefits of AWS CDK",
            href: "#cdk-benefits",
            level: 1
          },
          {
            text: "Use Cases in ResearchIQ",
            href: "#cdk-use-cases",
            level: 1
          },
          {
            text: "ResearchIQ Deployment Architecture",
            href: "#deployment-architecture",
            level: 1
          },
          {
            text: "Getting Started with AWS CDK",
            href: "#getting-started",
            level: 1
          }
        ]}
        ariaLabelledby="navigation-header"
      />
    </div>
  );
}

// Main DeploymentPage component with page-specific layout
function DeploymentPage() {
  return (
    <AppLayout
      content={<DeploymentPageContent />}
      tools={<DeploymentPageTools />}
      toolsHide={false}
      navigationHide={true}
    />
  );
}

export default DeploymentPage;
